import { getIn } from 'formik';
import checkCondition from './checkCondition';
import { CheckoutState, useCheckoutState } from './checkoutState';
import { CheckboxConfig, ConditionField, Conditions, config, DynamicFieldAreaKey, DynamicFields } from './config';

export type ConditionValues = Partial<Record<ConditionField, string | string[]>> & { basketTotal?: number };

export type DynamicFieldAreaBaseProps = {
  area: DynamicFieldAreaKey;
  context?: string;
  conditionValues?: ConditionValues;
  excludeNewsletterSignUp?: boolean;
  reviewFields?: DynamicFieldWithKeyProps[];
};

export type DynamicFieldType =
  | 'birthDate'
  | 'phoneNumber'
  | 'logisticsPhoneNumber'
  | 'taxId'
  | 'carrierCustomerId'
  | 'checkbox'
  | 'text';

export type DynamicFieldProps = { type: DynamicFieldType; name: string; dataTestId: string } & Partial<
  DynamicFields['birthDate'] &
    DynamicFields['phoneNumber'] &
    DynamicFields['logisticsPhoneNumber'] &
    DynamicFields['taxId'] &
    DynamicFields['carrierCustomerId'] &
    CheckboxConfig
>;

export type DynamicFieldWithKeyProps = DynamicFieldProps & { key: string };

const getCheckboxName = (word: string) => `is${word.charAt(0).toUpperCase() + word.slice(1)}Accepted`;

const getInitialCheckboxValues = (
  areas: DynamicFieldAreaKey[]
): Record<string, boolean> & {
  isNewsletterSignUpAccepted?: boolean;
} =>
  Object.fromEntries(
    areas
      .flatMap(area => config.dynamicFields[area]?.checkboxes ?? [])
      .map(checkbox => [getCheckboxName(checkbox.name), false])
  );

export const getInitialDynamicFieldValues = (
  checkoutState: CheckoutState | undefined,
  ...areas: DynamicFieldAreaKey[]
) => ({
  phoneNumber: checkoutState?.customer?.data.phone?.plainText ?? '',
  logisticsPhoneNumber: checkoutState?.orderState.shipping?.logisticsContact?.plainText ?? '',
  birthDate: checkoutState?.customer?.data.birthDate ?? '',
  taxId: checkoutState?.orderState.taxId ?? '',
  carrierCustomerId: '',
  ...getInitialCheckboxValues(areas)
});

export const getEnabledFields = ({
  area,
  context,
  conditionValues,
  excludeNewsletterSignUp
}: DynamicFieldAreaBaseProps): (DynamicFieldProps & { key: string })[] => {
  const isEnabled = ({ enabled, conditions }: { enabled?: boolean; conditions?: Conditions }) =>
    enabled !== undefined ?
      enabled && checkCondition(conditions?.enabled, conditionValues)
    : checkCondition(conditions?.enabled, conditionValues);

  const isRequired = ({ required, conditions }: { required?: boolean; conditions?: Conditions }) =>
    !conditions?.required ? required : checkCondition(conditions?.required, conditionValues);
  const {
    checkboxes: checkboxesConfig = [],
    texts: textsConfig = [],
    ...dynamicFieldsConfig
  } = config.dynamicFields[area] ?? {};
  return [
    ...Object.entries(dynamicFieldsConfig).map(([type, dynamicFieldConfig]) => ({
      type,
      key: type,
      ...dynamicFieldConfig
    })),
    ...checkboxesConfig.map(dynamicFieldConfig => ({
      type: 'checkbox',
      key: dynamicFieldConfig.name,
      ...dynamicFieldConfig
    })),
    ...textsConfig.map(dynamicFieldConfig => ({
      type: 'text',
      key: dynamicFieldConfig.name,
      ...dynamicFieldConfig
    }))
  ]
    .filter(({ type, key }) => !(type === 'checkbox' && key === 'newsletterSignUp' && excludeNewsletterSignUp))
    .filter(dynamicFieldConfig => isEnabled(dynamicFieldConfig))
    .map(({ type, key, ...dynamicFieldConfig }) => ({
      type: type as DynamicFieldType,
      key,
      ...dynamicFieldConfig,
      required: isRequired(dynamicFieldConfig),
      name: [context, type === 'checkbox' ? getCheckboxName(key) : key].filter(Boolean).join('.'),
      dataTestId: `${area}-${key}`
    }));
};

export const useDynamicFields = () => {
  const { setDynamicFields, checkoutState } = useCheckoutState();
  const {
    orderState: { basket },
    integrations: {
      newsletter: { customerIsConfirmedSubscriber }
    }
  } = checkoutState;

  const submitDynamicFieldArea = async (
    dynamicFieldArea: DynamicFieldAreaBaseProps,
    formValues: Record<string, any>,
    excludeNewsletterSignUp?: boolean
  ) => {
    const enabledFields = getEnabledFields({
      ...dynamicFieldArea,
      conditionValues: {
        ...dynamicFieldArea.conditionValues,
        basketTotal: basket.total.costCapture ?? basket.total.costWithTaxWithVouchersWithServiceCostsWithGiftCard
      },
      excludeNewsletterSignUp: excludeNewsletterSignUp || customerIsConfirmedSubscriber
    });

    const birthDateField = enabledFields.find(field => field.type === 'birthDate');
    const birthDate = birthDateField && getIn(formValues, birthDateField.name);

    const phoneNumberField = enabledFields.find(field => field.type === 'phoneNumber');
    const phoneNumber = phoneNumberField && getIn(formValues, phoneNumberField.name);

    const logisticsPhoneNumberField = enabledFields.find(field => field.type === 'logisticsPhoneNumber');
    const logisticsPhoneNumber = logisticsPhoneNumberField && getIn(formValues, logisticsPhoneNumberField.name);

    const taxIdField = enabledFields.find(field => field.type === 'taxId');
    const taxId = taxIdField && getIn(formValues, taxIdField.name);

    const newsletterSignUpField = enabledFields.find(
      field => field.type === 'checkbox' && field.key === 'newsletterSignUp'
    );
    const isNewsletterSignUpAccepted = newsletterSignUpField && getIn(formValues, newsletterSignUpField.name);

    if (birthDate || phoneNumber || logisticsPhoneNumber || taxIdField || isNewsletterSignUpAccepted) {
      await setDynamicFields({
        customer:
          birthDate || phoneNumber || logisticsPhoneNumber || isNewsletterSignUpAccepted ?
            {
              birthDate: birthDate || undefined,
              phone: phoneNumber || undefined,
              carrierPhone: logisticsPhoneNumber || undefined,
              hasSubscribedNewsletter: isNewsletterSignUpAccepted
            }
          : undefined,
        order: taxIdField ? { taxId } : undefined
      });
    }
  };

  const getCarrierCustomerId = (dynamicFieldArea: DynamicFieldAreaBaseProps, formValues: Record<string, any>) => {
    const enabledFields = getEnabledFields(dynamicFieldArea);
    const carrierCustomerIdField = enabledFields.find(field => field.type === 'carrierCustomerId');
    return carrierCustomerIdField && getIn(formValues, carrierCustomerIdField.name);
  };

  return { submitDynamicFieldArea, getCarrierCustomerId };
};
